import { ChangeEvent, Dispatch, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import { Value, isPossiblePhoneNumber } from 'react-phone-number-input';

import {
	CustomCheckBox,
	CustomInput,
	CustomPhoneInput,
	CustomSelect,
	CustomSwitch,
	MultipleSelect,
	Text,
} from '../../shared/uiComponents';
import { SelectDataProperties } from '../../shared/uiComponents/Dropdown';
import { capitalizer, validateEmail, validateName } from '../../shared/Helpers/functions';
import AddressInput from '../../shared/uiComponents/Input/addressInput';
import { AdminContainer as Container, Loader } from '../../components/StyledComponents';
import { InsuranceMultiSelect } from '../../components/InsuranceSelect';
import { AdminTypes } from '../../components/Menu';
import { PERMISSIONS } from '../../App/constants';
import ChangeEmail from './changeEmail';
import { ACTIONS, ActionProperties } from './adminReducer';
import DiscretionaryHoursAllocation from './DiscretionaryHoursAllocation';

import { DispatchProperties, useSelector } from '../../redux/store';
import { getRoles } from '../../redux/State/identitySlice/roleSlice';
import { SaveUserDataProperties } from '../../redux/API/userAPIProperties';
import { getAuthorizationTypes, getUserBranches, updateSaveDisabled } from '../../redux/State/userSlice';
import { getInsurances } from '../../redux/State/clientSlice/insuranceSlice';
import { getUserAccountDetails } from '../../redux/State/identitySlice/userSlice';

interface ErrorProperties {
	address: string;
	email: string;

	firstName: string;
	lastName: string;
	phoneNumber: string;
	role: string;
}

const AdminForm = ({
	adminInfo,
	setAdminInfo,
}: {
	adminInfo: SaveUserDataProperties;
	setAdminInfo: Dispatch<ActionProperties>;
}) => {
	const formType = window.localStorage.getItem('formType') ?? '';

	const dispatch = useDispatch<DispatchProperties>();
	const { userId } = useParams();

	const [error, setError] = useState<ErrorProperties>({
		firstName: '',
		lastName: '',
		email: '',
		phoneNumber: '',
		address: '',
		role: '',
	});

	const loading = useSelector((state) => state.user.loading);
	const loadingRoles = useSelector((state) => state.role.loading);
	const roles = useSelector((state) => state.role.roles);
	const role = useSelector((state) => state.account.role);
	const authorizationTypes = useSelector((state) => state.userAuthorization.authorizationTypes);
	const insurances = useSelector((state) => state.insurance.insurances);
	const branches = useSelector((state) => state.userBranch.userBranches);
	const id = useSelector((state) => state.account.user.id);

	const isSuperAdmin = role?.claims.some((claim) => claim.claimValue === PERMISSIONS.SUPERADMIN);

	// Compute available roles based on formType and permissions
	const adminRoles = useMemo(() => {
		if (!roles?.length || !formType) return [];

		// For BT forms, no role selection needed (set automatically)
		if (AdminTypes[formType] === AdminTypes.bt) return [];

		// For superadmin editing existing non-admin user, show all roles
		if (isSuperAdmin && userId && AdminTypes[formType] !== AdminTypes.admin) {
			return roles.filter((singleRole) => singleRole.section.id !== AdminTypes.bt);
		}

		// Default: filter roles by formType section
		return roles.filter((singleRole) => singleRole.section.id === AdminTypes[formType]);
	}, [roles, formType, isSuperAdmin, userId]);

	// Compute the chosen role's section based on selected roleId
	const choosenRole = useMemo(() => {
		if (!adminInfo.roleId || !roles || !roles.length) return 0;
		const selectedRole = roles.find((x) => x.id === adminInfo.roleId);

		return selectedRole?.section.id ?? 0;
	}, [adminInfo.roleId, roles]);

	useEffect(() => {
		if (!userId) return;
		dispatch(getUserAccountDetails(userId));
	}, [userId, dispatch]);

	useEffect(() => {
		if (authorizationTypes.length) return;
		dispatch(getAuthorizationTypes());
	}, [authorizationTypes, dispatch]);

	useEffect(() => {
		dispatch(getInsurances({ includeOutOfNetwork: false }));
	}, [dispatch]);

	useEffect(() => {
		if (!id) return;
		dispatch(getUserBranches(id));
	}, [dispatch, id]);

	useEffect(() => {
		// Auto-set role for BT form (readonly default role)
		if (!roles?.length || !formType || AdminTypes[formType] !== AdminTypes.bt) return;
		const btRoles = roles.filter((singleRole) => singleRole.section.id === AdminTypes.bt);
		const defaultRole = btRoles.find((x) => x.isReadonly);
		if (!defaultRole) return;
		setAdminInfo({ type: ACTIONS.setRoleId, payload: defaultRole.id });
	}, [roles, formType, setAdminInfo]);

	useEffect(() => {
		if (roles) return;
		dispatch(getRoles());
	}, [roles, dispatch]);

	useEffect(() => {
		const { firstName, lastName, email, phoneNumber, address, roleId, authorizationType } = adminInfo;
		const values = [firstName, lastName, email, phoneNumber, address, roleId];
		const hasInitialData = Object.values(error).some((x) => Boolean(x)) || values.some((x) => !x);

		if (
			!authorizationType &&
			Boolean(formType) &&
			(AdminTypes.bcba === AdminTypes[formType] || AdminTypes.cs === AdminTypes[formType]) &&
			(choosenRole === AdminTypes.bcba || choosenRole === AdminTypes.cs)
		) {
			dispatch(updateSaveDisabled(true));

			return;
		}
		dispatch(updateSaveDisabled(hasInitialData));
	}, [error, adminInfo, formType, choosenRole, setAdminInfo, dispatch]);

	useEffect(() => {
		if (!formType || AdminTypes.bt !== AdminTypes[formType]) return;
		setAdminInfo({ type: ACTIONS.setAuthorizationType, payload: null });
	}, [formType, setAdminInfo]);

	// Auto-assign all branches to admin role
	useEffect(() => {
		if (!formType || AdminTypes[formType] !== AdminTypes.admin) return;
		if (!branches || branches.length === 0) return;

		// Automatically assign all branches to admin
		const allBranchIds = branches.map((branch) => branch.id);
		setAdminInfo({ type: ACTIONS.setBranchIds, payload: allBranchIds });
	}, [formType, branches, setAdminInfo]);

	const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setAdminInfo({ type: ACTIONS.setFirstName, payload: capitalizer(value) });
		setError((prev) => ({ ...prev, firstName: '' }));
	};

	const onLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setAdminInfo({ type: ACTIONS.setLastName, payload: capitalizer(value) });
		setError((prev) => ({ ...prev, lastName: '' }));
	};

	const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setAdminInfo({ type: ACTIONS.setEmail, payload: value });
		setError((prev) => ({ ...prev, email: '' }));
	};

	const onPhoneNumberChange = (value: Value) => {
		setAdminInfo({ type: ACTIONS.setPhoneNumber, payload: value });
		setError((prev) => ({ ...prev, phoneNumber: '' }));
	};

	const onAddressChange = ({ fullAddress }: { city?: string; fullAddress?: string; zipcode?: string }) => {
		setAdminInfo({ type: ACTIONS.setAddress, payload: fullAddress });
		setError((prev) => ({ ...prev, address: '' }));
	};

	const onRoleChange = (event: SelectChangeEvent<string>) => {
		const value = event.target.value;
		if (!value) return;
		setAdminInfo({ type: ACTIONS.setRoleId, payload: value });
		setError((prev) => ({ ...prev, role: '' }));
	};

	const onAuthorizationTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name } = event.target;
		if (!name) return;
		const payload = parseInt(name);
		setAdminInfo({ type: ACTIONS.setAuthorizationType, payload });

		if (payload === 2) {
			// limited permit
			setAdminInfo({ type: ACTIONS.setInsurances, payload: [] });
		}
	};

	const onInsurancesChange = (value: string[]) => {
		if (!value || typeof value === 'string') return;
		setAdminInfo({ type: ACTIONS.setInsurances, payload: value });
	};

	const onMBHIdChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setAdminInfo({ type: ACTIONS.setMBHId, payload: value });
	};

	const onDOEIdChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setAdminInfo({ type: ACTIONS.setDOEId, payload: value });
	};

	const onFirstNameBlurHandler = () => {
		if (Boolean(adminInfo.firstName) && validateName(adminInfo.firstName)) return;

		if (!adminInfo.firstName) {
			setError((prev) => ({ ...prev, firstName: 'Required' }));

			return;
		}
		setError((prev) => ({ ...prev, firstName: 'Invalid name' }));
	};

	const onLastNameBlurHandler = () => {
		if (Boolean(adminInfo.lastName) && validateName(adminInfo.lastName)) return;

		if (!adminInfo.lastName) {
			setError((prev) => ({ ...prev, lastName: 'Required' }));

			return;
		}
		setError((prev) => ({ ...prev, lastName: 'Invalid name' }));
	};

	const onEmailBlurHandler = () => {
		const { email } = adminInfo;

		if (!email) {
			setError((prev) => ({ ...prev, email: 'Required' }));

			return;
		}

		if (!validateEmail(email)) {
			setError((prev) => ({ ...prev, email: 'Invalid email' }));

			return;
		}
		setError((prev) => ({ ...prev, email: '' }));
	};

	const onPhoneNumberBlurHandler = () => {
		const { phoneNumber } = adminInfo;

		if (!phoneNumber) {
			setError((prev) => ({ ...prev, phoneNumber: 'Required' }));

			return;
		}

		if (!isPossiblePhoneNumber(phoneNumber)) {
			setError((prev) => ({ ...prev, phoneNumber: 'Invalid phone number' }));

			return;
		}
	};

	const onAddressBlurHandler = () => {
		if (adminInfo.address) return;
		setError((prev) => ({ ...prev, address: 'Required' }));
	};

	const onRoleBlurHandler = () => {
		if (adminInfo.roleId) return;
		setError((prev) => ({ ...prev, role: 'Required' }));
	};

	const insuranceData = [{ id: '', name: 'N/A', subInsurances: [], branches: [] }, ...insurances];

	const onBranchChange = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setAdminInfo({ type: ACTIONS.setBranchIds, payload: value });
	};

	const assignPermissionHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setAdminInfo({
			type: ACTIONS.setCanBeAssignedToClient,
			payload: checked,
		});

		if (!checked) {
			setAdminInfo({ type: ACTIONS.setInsurances, payload: [] });
			setAdminInfo({ type: ACTIONS.setAuthorizationType, payload: null });
		}
	};

	return !loading && !loadingRoles ? (
		<Container>
			<Text
				title={`${formType ? formType : 'User'} information`.toUpperCase()}
				type="h4"
				font="header"
				fontWeight="semiBold"
				className="marginBottom16"
			/>
			<CustomInput
				label="First name"
				value={adminInfo.firstName}
				onChange={onFirstNameChange}
				className="marginBottom16"
				onBlur={onFirstNameBlurHandler}
				error={Boolean(error.firstName)}
				errorMessage={error.firstName}
			/>
			<CustomInput
				label="Last name"
				value={adminInfo.lastName}
				onChange={onLastNameChange}
				className="marginBottom16"
				onBlur={onLastNameBlurHandler}
				error={Boolean(error.lastName)}
				errorMessage={error.lastName}
			/>
			<div style={{ display: 'flex', gap: '8px', width: '100%' }}>
				<div style={{ width: '100%' }}>
					<CustomInput
						label="Email"
						value={adminInfo.email}
						onChange={onEmailChange}
						className="marginBottom16"
						disabled={Boolean(userId)}
						error={Boolean(error.email)}
						errorMessage={error.email}
						onBlur={onEmailBlurHandler}
					/>
				</div>
				{Boolean(userId) && <ChangeEmail />}
			</div>
			<CustomPhoneInput
				label="Phone number"
				value={adminInfo.phoneNumber}
				onChange={onPhoneNumberChange}
				className="marginBottom16"
				onBlur={onPhoneNumberBlurHandler}
				error={Boolean(error.phoneNumber)}
				errorMessage={error.phoneNumber}
			/>
			<AddressInput
				setAddressDetails={onAddressChange}
				value={adminInfo.address}
				handleInputBlur={onAddressBlurHandler}
			/>
			<CustomInput label="MBH ID" value={adminInfo.mbhId} onChange={onMBHIdChange} className="marginBottom16" />
			{AdminTypes[formType] === AdminTypes.bt && (
				<CustomInput label="DOE ID" value={adminInfo.doeId} onChange={onDOEIdChange} className="marginBottom16" />
			)}
			{AdminTypes[formType] !== AdminTypes.admin && (
				<MultipleSelect
					label="Branches"
					data={branches}
					setValue={onBranchChange}
					initialValue={adminInfo.branchIds}
					description="Leave empty to auto-assign to default branch"
					className="marginBottom16"
				/>
			)}
			{AdminTypes[formType] !== AdminTypes.bt && (
				<>
					<CustomSelect
						data={adminRoles as SelectDataProperties[]}
						value={adminInfo.roleId}
						setValue={onRoleChange}
						label="Role"
						className="marginBottom16"
						onBlur={onRoleBlurHandler}
						error={Boolean(error.role)}
						errorMessage={error.role}
					/>
					{AdminTypes[formType] === AdminTypes.admin && (
						<CustomSwitch
							labelPlacement="right"
							label="Can be assigned to client"
							checked={Boolean(adminInfo.canBeAssignedToClient)}
							onChange={assignPermissionHandler}
							style={{ marginBottom: '16px' }}
						/>
					)}
					{Boolean(adminInfo.canBeAssignedToClient) && (
						<>
							<div className="marginLeft8 marginBottom8">
								<Text className="inputLabel active" title="Type of authorization: " type="psm" fontWeight="medium" />
								{authorizationTypes.map((type) => (
									<CustomCheckBox
										key={type.id}
										item={{
											id: `${type.id}`,
											label: type.name,
											checked: type.id === adminInfo.authorizationType,
										}}
										onChange={onAuthorizationTypeChange}
									/>
								))}
							</div>
							<div style={{ maxWidth: '450px' }}>
								<InsuranceMultiSelect
									label="Select insurance (multiple if applicable): "
									data={insuranceData}
									value={adminInfo.insurances}
									setValue={onInsurancesChange}
									selectedBranchIds={adminInfo.branchIds}
									userBranches={branches}
									className="marginBottom16"
									disabled={adminInfo.authorizationType !== 1}
								/>
							</div>
						</>
					)}
				</>
			)}
			{AdminTypes[formType] === AdminTypes.admin && (
				<div className="marginBottom16">
					<CustomSwitch
						labelPlacement="right"
						label="Case Manager"
						checked={adminInfo.isCaseCoordinator}
						onChange={(event) => {
							setAdminInfo({
								type: ACTIONS.setCaseCoordinator,
								payload: event.target.checked,
							});
						}}
					/>
				</div>
			)}

			{[AdminTypes.cs, AdminTypes.admin, AdminTypes.bcba, AdminTypes.bt].includes(choosenRole) && Boolean(userId) && <DiscretionaryHoursAllocation />}
		</Container>
	) : (
		<Loader />
	);
};

export default AdminForm;
